import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, Calendar, Car, Briefcase, FolderPlus, MapPin, X, Hash, Upload, FileText, Sparkles } from "lucide-react";

export default function WorkerFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [workerTypes, setWorkerTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [globalStatuses, setGlobalStatuses] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    worker_type_id: "",
    position: "",
    position_experience: "",
    global_status: "Feldolgozatlan", // Alapértelmezett: még nem volt kommunikáció
    address: "",
    email: "",
    experience: "",
    notes: "",
    work_type: "",
    has_car: "",
    latitude: null,
    longitude: null,
    properties: [] // Tulajdonságok: megbízható, jó munkaerő, stb.
  });
  
  // Elérhető tulajdonságok
  const availableProperties = [
    { id: "megbizhato", label: "Megbízható", color: "bg-green-500" },
    { id: "jo_munkaero", label: "Jó munkaerő", color: "bg-blue-500" },
    { id: "rossz_minoseg", label: "Rossz minőség", color: "bg-red-500" },
    { id: "rovidtavu", label: "Rövidtávú munkaerő", color: "bg-orange-500" }
  ];
  
  // Project assignment state
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedTrial, setSelectedTrial] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [addToProject, setAddToProject] = useState("waitlist");
  
  // ÚJ: Projekt választó dialog státuszhoz
  const [projectSelectDialog, setProjectSelectDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedPositionIds, setSelectedPositionIds] = useState([]); // Pozíciók
  const [projectPositions, setProjectPositions] = useState([]); // Projekt pozíciói
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingProjectAssignment, setPendingProjectAssignment] = useState(null); // Új dolgozóhoz
  
  // Map dialog state
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  
  // CV Import state
  const [cvImportDialog, setCvImportDialog] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvParsing, setCvParsing] = useState(false);
  const fileInputRef = useRef(null);
  
  // Duplikáció kezelés state
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null); // {existing_worker, new_worker_data}

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectTrials(selectedProject);
    } else {
      setTrials([]);
      setSelectedTrial("");
    }
  }, [selectedProject]);

  const fetchProjectTrials = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/trials`);
      setTrials(res.data);
    } catch (e) {
      console.error("Error fetching trials:", e);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [typesRes, projectsRes, statusesRes, globalStatusesRes] = await Promise.all([
        axios.get(`${API}/worker-types`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/statuses`),
        axios.get(`${API}/global-statuses`) // ÚJ: Globális státuszok lekérése
      ]);
      setWorkerTypes(typesRes.data);
      setProjects(projectsRes.data.filter(p => !p.is_closed));
      setStatuses(statusesRes.data);
      setGlobalStatuses(globalStatusesRes.data || []); // ÚJ: Globális státuszok
      
      if (isEdit) {
        const workerRes = await axios.get(`${API}/workers/${id}`);
        setFormData({
          name: workerRes.data.name || "",
          phone: workerRes.data.phone || "",
          worker_type_id: workerRes.data.worker_type_id || "",
          position: workerRes.data.position || "",
          position_experience: workerRes.data.position_experience || "",
          global_status: workerRes.data.global_status || "Feldolgozatlan",
          address: workerRes.data.address || "",
          email: workerRes.data.email || "",
          experience: workerRes.data.experience || "",
          notes: workerRes.data.notes || "",
          work_type: workerRes.data.work_type || "",
          has_car: workerRes.data.has_car || "",
          latitude: workerRes.data.latitude || null,
          longitude: workerRes.data.longitude || null,
          properties: workerRes.data.properties || []
        });
      }
    } catch (e) {
      toast.error("Hiba");
      if (isEdit) navigate("/workers");
    }
  };

  // Geocode search
  const handleMapSearch = async () => {
    if (!mapSearchQuery.trim()) return;
    setGeocoding(true);
    try {
      const res = await axios.post(`${API}/geocode`, { address: mapSearchQuery });
      if (res.data.latitude && res.data.longitude) {
        setMapSearchResults([{
          display_name: res.data.display_name || mapSearchQuery,
          lat: res.data.latitude,
          lon: res.data.longitude,
          county: res.data.county || ""
        }]);
      } else {
        setMapSearchResults([]);
        toast.error("Nem található a cím");
      }
    } catch (e) {
      toast.error("Hiba a keresésben");
      setMapSearchResults([]);
    } finally {
      setGeocoding(false);
    }
  };

  const selectLocationFromMap = (location) => {
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      address: location.display_name,
      latitude: parseFloat(location.lat),
      longitude: parseFloat(location.lon)
    }));
    setMapDialogOpen(false);
    toast.success("Hely kiválasztva");
  };

  // ÚJ: Státusz választáskor ellenőrzés
  const handleStatusChange = (newStatus) => {
    // Ha projekt-specifikus státusz, akkor projekt és pozíció választás kell
    const projectStatuses = ["Dolgozik", "Próbára vár", "Próba megbeszélve"];
    if (projectStatuses.includes(newStatus)) {
      setPendingStatus(newStatus);
      setSelectedProjectId("");
      setSelectedPositionIds([]);
      setProjectPositions([]);
      setProjectSelectDialog(true);
    } else {
      // Globális státusz (Feldolgozatlan, Tiltólista) - csak beállítás, nincs projekt/pozíció
      setFormData({...formData, global_status: newStatus});
    }
  };
  
  // Projekt változáskor pozíciók betöltése
  const handleProjectChange = async (projectId) => {
    setSelectedProjectId(projectId);
    setSelectedPositionIds([]);
    if (projectId) {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/positions`);
        setProjectPositions(res.data || []);
      } catch (e) {
        console.error("Error fetching positions:", e);
        setProjectPositions([]);
      }
    } else {
      setProjectPositions([]);
    }
  };
  
  // Projekt választás megerősítése
  const handleProjectSelection = async () => {
    if (!selectedProjectId) {
      toast.error("Válassz projektet!");
      return;
    }
    
    // Pozíció kötelező a projekt-specifikus státuszoknál
    if (selectedPositionIds.length === 0) {
      toast.error(`A "${pendingStatus}" státuszhoz kötelező legalább egy pozíciót választani!`);
      return;
    }
    
    // Státusz ID lekérése a pendingStatus alapján
    const statusObj = statuses.find(s => s.name === pendingStatus);
    if (!statusObj) {
      toast.error("Státusz nem található!");
      return;
    }
    
    // Formdata státusz beállítása
    setFormData({...formData, global_status: pendingStatus});
    
    // Projekt worker kapcsolat létrehozása (ha van dolgozó ID - edit módban)
    if (isEdit && id) {
      try {
        await axios.post(`${API}/projects/${selectedProjectId}/workers`, {
          worker_id: id,
          status_id: statusObj.id,
          position_ids: selectedPositionIds
        });
        toast.success(`Dolgozó hozzáadva a projekthez: ${pendingStatus}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Hiba történt");
      }
    } else {
      // Új dolgozónál később használjuk
      setPendingProjectAssignment({
        projectId: selectedProjectId,
        statusId: statusObj.id,
        positionIds: selectedPositionIds
      });
      toast.success("Projekt és pozíció beállítva, mentéskor hozzáadásra kerül");
    }
    
    setProjectSelectDialog(false);
    setSelectedProjectId("");
    setSelectedPositionIds([]);
    setProjectPositions([]);
    setPendingStatus("");
  };
  
  // CV Import funkcionalitás
  const handleCvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['.pdf', '.docx', '.txt', '.xlsx', '.xls'];
    const isValid = validTypes.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!isValid) {
      toast.error("Csak PDF, DOCX, TXT, XLSX, XLS fájlok támogatottak");
      return;
    }
    
    setCvFile(file);
  };
  
  const handleCvParse = async () => {
    if (!cvFile) {
      toast.error("Válassz ki egy fájlt");
      return;
    }
    
    setCvParsing(true);
    
    try {
      const formData = new FormData();
      formData.append("file", cvFile);
      
      const res = await axios.post(`${API}/workers/import/cv-parse`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const extracted = res.data.extracted_data;
      
      // Kitöltjük az űrlapot az AI által kinyert adatokkal
      setFormData(prev => ({
        ...prev,
        name: extracted.name || prev.name,
        phone: extracted.phone || prev.phone,
        email: extracted.email || prev.email,
        address: extracted.address || prev.address,
        position: extracted.position || prev.position,
        experience: extracted.experience || prev.experience,
        notes: extracted.notes 
          ? (prev.notes ? `${prev.notes}\n\n${extracted.notes}` : extracted.notes)
          : prev.notes
      }));
      
      // Ha van skills, azt is hozzáadjuk a notes-hoz
      if (extracted.skills && extracted.skills.length > 0) {
        const skillsText = `Készségek: ${extracted.skills.join(', ')}`;
        setFormData(prev => ({
          ...prev,
          notes: prev.notes 
            ? `${prev.notes}\n\n${skillsText}` 
            : skillsText
        }));
      }
      
      toast.success("✅ CV sikeresen elemezve AI-val! Ellenőrizd és módosítsd az adatokat.");
      setCvImportDialog(false);
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      
    } catch (e) {
      console.error("CV parse error:", e);
      toast.error(e.response?.data?.detail || "Hiba a CV feldolgozása során");
    } finally {
      setCvParsing(false);
    }
  };
  
  const handleSubmit = async (e, forceCreate = false) => {
    if (e) e.preventDefault();
    
    if (formData.name.length < 2) {
      toast.error("Név minimum 2 karakter");
      return;
    }
    if (!formData.phone) {
      toast.error("Telefonszám kötelező");
      return;
    }
    if (!formData.worker_type_id) {
      toast.error("Válassz típust");
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData, force_create: forceCreate };
      
      // Project assignment
      if (!isEdit && selectedProject && addToProject === "direct" && selectedStatus) {
        payload.project_id = selectedProject;
        payload.initial_status = selectedStatus;
      } else if (!isEdit && selectedProject && addToProject === "waitlist") {
        payload.project_id = selectedProject;
      }
      
      let workerId;
      
      if (isEdit) {
        await axios.put(`${API}/workers/${id}`, payload);
        toast.success("Mentve");
        workerId = id;
      } else {
        const res = await axios.post(`${API}/workers`, payload);
        workerId = res.data.id;
        toast.success("Létrehozva");
        
        // Ha státuszválasztásból jött projekt hozzárendelés
        if (pendingProjectAssignment) {
          try {
            await axios.post(`${API}/projects/${pendingProjectAssignment.projectId}/workers`, {
              worker_id: workerId,
              status_id: pendingProjectAssignment.statusId,
              position_ids: pendingProjectAssignment.positionIds || []
            });
            toast.success("Dolgozó hozzáadva a projekthez");
          } catch (error) {
            toast.error("Hiba a projekthez adásnál: " + (error.response?.data?.detail || ""));
          }
        }
        
        // If trial is selected, add worker to trial
        if (selectedProject && selectedTrial) {
          // First add to project if not already
          if (addToProject === "waitlist") {
            try {
              await axios.post(`${API}/projects/${selectedProject}/workers`, { worker_id: workerId });
            } catch (e) {
              // May already be added
            }
          }
          // Add to trial
          await axios.post(`${API}/projects/${selectedProject}/trials/${selectedTrial}/workers`, { 
            worker_id: workerId 
          });
          toast.success("Dolgozó hozzáadva a próbához is");
        }
      }
      navigate("/workers");
    } catch (e) {
      // Duplikáció kezelés (409-es hiba)
      if (e.response?.status === 409 && e.response?.data?.detail?.type === "duplicate") {
        setDuplicateData(e.response.data.detail);
        setDuplicateDialog(true);
        setLoading(false);
        return;
      }
      
      // Pydantic validation error kezelés
      const errorData = e.response?.data;
      let errorMessage = "Hiba történt";
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.join('.') || 'mező';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
        }
      }
      
      console.error("Worker create error:", errorData);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Duplikáció: Új létrehozása mindenképp
  const handleForceCreate = () => {
    setDuplicateDialog(false);
    handleSubmit(null, true);
  };
  
  // Duplikáció: Régi frissítése az új adatokkal
  const handleUpdateExisting = async () => {
    if (!duplicateData?.existing_worker?.id) return;
    
    setLoading(true);
    try {
      const updatePayload = {
        ...duplicateData.new_worker_data,
        // Megtartjuk az eredeti worker_type_id-t ha az új üres
        worker_type_id: duplicateData.new_worker_data.worker_type_id || duplicateData.existing_worker.worker_type_id
      };
      
      await axios.put(`${API}/workers/${duplicateData.existing_worker.id}`, updatePayload);
      toast.success("Meglévő dolgozó frissítve!");
      setDuplicateDialog(false);
      navigate("/workers");
    } catch (e) {
      toast.error("Hiba a frissítésnél: " + (e.response?.data?.detail || ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/workers")} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">{isEdit ? "Szerkesztés" : "Új dolgozó"}</h1>
        </div>
        
        {/* CV Import gomb - csak új dolgozónál */}
        {!isEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCvImportDialog(true)}
            className="gap-2"
          >
            <Sparkles className="w-4 h-4" />
            CV Import AI
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Dolgozó adatai</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Név *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Kiss János" required className="h-9" data-testid="worker-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Telefon *</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+36 20 123 4567" required className="h-9" data-testid="worker-phone-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Típus *</Label>
                <Select value={formData.worker_type_id} onValueChange={(v) => setFormData({...formData, worker_type_id: v})}>
                  <SelectTrigger className="h-9" data-testid="worker-type-select"><SelectValue placeholder="Válassz" /></SelectTrigger>
                  <SelectContent>{workerTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Státusz *</Label>
                <Select value={formData.global_status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Feldolgozatlan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Feldolgozatlan">⚪ Feldolgozatlan</SelectItem>
                    <SelectItem value="Próbára vár">🟠 Próbára vár</SelectItem>
                    <SelectItem value="Próba megbeszélve">🟣 Próba megbeszélve</SelectItem>
                    <SelectItem value="Dolgozik">🟢 Dolgozik</SelectItem>
                    <SelectItem value="Tiltólista">🔴 Tiltólista</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Pozíció (szabadon beírható)</Label>
                <Input value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})} placeholder="pl. Hegesztő, CNC gépkezelő" className="h-9" data-testid="worker-position-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@email.hu" className="h-9" data-testid="worker-email-input" />
              </div>
            </div>

            {/* Tulajdonságok / Hashtagek */}
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-1">
                <Hash className="w-3 h-3" />
                Tulajdonságok
              </Label>
              <div className="flex flex-wrap gap-2">
                {availableProperties.map(prop => {
                  const isSelected = formData.properties?.includes(prop.id);
                  return (
                    <button
                      key={prop.id}
                      type="button"
                      onClick={() => {
                        const newProps = isSelected
                          ? formData.properties.filter(p => p !== prop.id)
                          : [...(formData.properties || []), prop.id];
                        setFormData({...formData, properties: newProps});
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected 
                          ? `${prop.color} text-white shadow-md` 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`property-${prop.id}`}
                    >
                      #{prop.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Munkavégzés típusa és Saját autó */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  Munkavégzés típusa
                </Label>
                <Select value={formData.work_type} onValueChange={(v) => setFormData({...formData, work_type: v})}>
                  <SelectTrigger className="h-9" data-testid="worker-work-type-select">
                    <SelectValue placeholder="Válassz..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ingázó">Ingázó</SelectItem>
                    <SelectItem value="Szállásos">Szállásos</SelectItem>
                    <SelectItem value="Mindegy">Mindegy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm flex items-center gap-1">
                  <Car className="w-3 h-3" />
                  Saját autó
                </Label>
                <Select value={formData.has_car} onValueChange={(v) => setFormData({...formData, has_car: v})}>
                  <SelectTrigger className="h-9" data-testid="worker-has-car-select">
                    <SelectValue placeholder="Válassz..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Van">Van</SelectItem>
                    <SelectItem value="Nincs">Nincs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Pozíció tapasztalat</Label>
              <Textarea value={formData.position_experience} onChange={(e) => setFormData({...formData, position_experience: e.target.value})} placeholder="A pozícióval kapcsolatos tapasztalat, képzettség..." rows={2} data-testid="worker-position-exp-input" />
            </div>

            {/* Lakóhely térkép ikonnal */}
            <div className="space-y-1">
              <Label className="text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Lakóhely
              </Label>
              <div className="flex gap-2">
                <Input 
                  value={formData.address} 
                  onChange={(e) => setFormData({...formData, address: e.target.value, latitude: null, longitude: null})} 
                  placeholder="Budapest, Fő utca 1." 
                  className="h-9 flex-1" 
                  data-testid="worker-address-input" 
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  className="h-9 w-9 shrink-0" 
                  onClick={() => {
                    setMapSearchQuery(formData.address);
                    setMapDialogOpen(true);
                  }}
                  data-testid="open-map-btn"
                >
                  <MapPin className="w-4 h-4 text-primary" />
                </Button>
              </div>
              {formData.latitude && formData.longitude && (
                <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  Koordináták: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Általános tapasztalat</Label>
              <Textarea value={formData.experience} onChange={(e) => setFormData({...formData, experience: e.target.value})} placeholder="Korábbi munkatapasztalatok..." rows={2} data-testid="worker-experience-input" />
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Megjegyzések</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Egyéb..." rows={2} data-testid="worker-notes-input" />
            </div>

            {/* CSAK EDIT MODE-BAN: Projekthez hozzáadás */}
            {isEdit && (
              <div className="border-t border-border pt-4 mt-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <FolderPlus className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Projekthez hozzáadás</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Projekt</Label>
                    <Select 
                      value={selectedProject} 
                      onValueChange={(val) => {
                        setSelectedProject(val);
                        setSelectedTrial("");
                        setSelectedStatus("");
                      }}
                    >
                      <SelectTrigger className="h-9" data-testid="project-select">
                        <SelectValue placeholder="Válassz projektet..." />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} {p.date && `- ${p.date}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedProject && (
                    <>
                      {/* Hova adjuk hozzá */}
                      <div className="space-y-1">
                        <Label className="text-sm">Hova kerüljön a dolgozó?</Label>
                        <Select value={addToProject} onValueChange={setAddToProject}>
                          <SelectTrigger className="h-9" data-testid="add-to-project-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="waitlist">Várólistára</SelectItem>
                            <SelectItem value="direct">Közvetlenül a projektbe (státusszal)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Ha közvetlen, státusz választás */}
                      {addToProject === "direct" && (
                        <div className="space-y-1">
                          <Label className="text-sm">Kezdő státusz *</Label>
                          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="h-9" data-testid="initial-status-select">
                              <SelectValue placeholder="Válassz státuszt..." />
                            </SelectTrigger>
                            <SelectContent>
                              {statuses.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Próba választás - MINDIG megjelenik ha van próba */}
                      {trials.length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-sm flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Próbához csatolás (opcionális)
                          </Label>
                          <Select value={selectedTrial} onValueChange={setSelectedTrial}>
                            <SelectTrigger className="h-9" data-testid="trial-select">
                              <SelectValue placeholder="Válassz próbát..." />
                            </SelectTrigger>
                            <SelectContent>
                              {trials.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.date} {t.time && `(${t.time})`} - {t.notes || "Próba"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {selectedProject && (
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                    {addToProject === "direct" 
                      ? selectedStatus 
                        ? `A dolgozó közvetlenül a projekthez kerül "${statuses.find(s => s.id === selectedStatus)?.name || ''}" státusszal.${selectedTrial ? " + Próbához csatolva." : ""}`
                        : "Válassz státuszt a közvetlen hozzáadáshoz."
                      : selectedTrial 
                        ? "A dolgozó a várólistára kerül és a kiválasztott próbához is csatolódik."
                        : "A dolgozó a projekt várólistájára kerül."
                    }
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => navigate("/workers")}>Mégse</Button>
              <Button type="submit" size="sm" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-worker-btn">
                {loading ? "..." : <><Save className="w-4 h-4 mr-1" />{isEdit ? "Mentés" : "Létrehozás"}</>}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Map Dialog */}
      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Hely kiválasztás térképen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={mapSearchQuery}
                onChange={(e) => setMapSearchQuery(e.target.value)}
                placeholder="Keress címre, városra..."
                className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleMapSearch())}
              />
              <Button onClick={handleMapSearch} disabled={geocoding}>
                {geocoding ? "..." : "Keresés"}
              </Button>
            </div>

            {mapSearchResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Találatok:</p>
                {mapSearchResults.map((loc, idx) => (
                  <div 
                    key={idx}
                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => selectLocationFromMap(loc)}
                  >
                    <p className="font-medium text-sm">{loc.display_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {loc.lat.toFixed(4)}, {loc.lon.toFixed(4)}
                      {loc.county && ` • ${loc.county}`}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Simple map preview using OpenStreetMap embed */}
            {selectedLocation && (
              <div className="mt-4">
                <iframe
                  title="map"
                  width="100%"
                  height="200"
                  frameBorder="0"
                  scrolling="no"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedLocation.lon - 0.01}%2C${selectedLocation.lat - 0.01}%2C${selectedLocation.lon + 0.01}%2C${selectedLocation.lat + 0.01}&layer=mapnik&marker=${selectedLocation.lat}%2C${selectedLocation.lon}`}
                  className="rounded-lg border"
                ></iframe>
              </div>
            )}

            {mapSearchResults.length === 0 && !geocoding && (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Keress rá egy címre vagy városra</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Projekt és pozíció választó dialog státuszhoz */}
      <Dialog open={projectSelectDialog} onOpenChange={(open) => {
        if (!open) {
          setSelectedProjectId("");
          setSelectedPositionIds([]);
          setProjectPositions([]);
          setPendingStatus("");
        }
        setProjectSelectDialog(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Projekt és pozíció választás</DialogTitle>
            <DialogDescription>
              A "{pendingStatus}" státuszhoz válaszd ki a projektet és a pozíciót!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Projekt *</Label>
              <Select value={selectedProjectId} onValueChange={handleProjectChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Válassz projektet..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {p.client || ""} ({new Date(p.date).toLocaleDateString('hu-HU')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pozíció választó - csak ha van projekt */}
            {selectedProjectId && (
              <div className="space-y-2">
                <Label>Pozíció(k) * <span className="text-xs text-muted-foreground">(kötelező)</span></Label>
                {projectPositions.length > 0 ? (
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
                    {projectPositions.map(pos => (
                      <Button
                        key={pos.id}
                        type="button"
                        variant={selectedPositionIds.includes(pos.id) ? "default" : "outline"}
                        size="sm"
                        className={selectedPositionIds.includes(pos.id) ? "bg-indigo-600" : ""}
                        onClick={() => {
                          if (selectedPositionIds.includes(pos.id)) {
                            setSelectedPositionIds(selectedPositionIds.filter(id => id !== pos.id));
                          } else {
                            setSelectedPositionIds([...selectedPositionIds, pos.id]);
                          }
                        }}
                      >
                        {pos.name} ({pos.current_count || 0}/{pos.count})
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-amber-600 p-2 bg-amber-50 rounded border border-amber-200">
                    Nincs pozíció ehhez a projekthez. Először adj hozzá pozíciókat a projekt beállításoknál!
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setProjectSelectDialog(false);
              setSelectedProjectId("");
              setSelectedPositionIds([]);
              setProjectPositions([]);
              setPendingStatus("");
            }}>
              Mégse
            </Button>
            <Button 
              onClick={handleProjectSelection} 
              disabled={!selectedProjectId || selectedPositionIds.length === 0}
            >
              Megerősítés
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CV Import Dialog */}
      <Dialog open={cvImportDialog} onOpenChange={setCvImportDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              CV/Excel Import AI-val
            </DialogTitle>
            <DialogDescription>
              Töltsd fel a CV-t vagy Excel-t, és az AI automatikusan kinyeri az adatokat!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Támogatott fájlok */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                📄 Támogatott fájlok:
              </p>
              <div className="flex flex-wrap gap-2">
                {['PDF', 'DOCX', 'TXT', 'XLSX', 'XLS'].map(type => (
                  <span key={type} className="text-xs bg-white dark:bg-blue-950 px-2 py-1 rounded border border-blue-200 dark:border-blue-800">
                    {type}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Fájl feltöltés */}
            <div className="space-y-2">
              <Label className="text-sm">Válassz fájlt</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.xlsx,.xls"
                  onChange={handleCvFileSelect}
                  className="flex-1"
                />
                {cvFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setCvFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {cvFile && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {cvFile.name} ({(cvFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            
            {/* Mit kinyeri az AI */}
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium mb-2">🤖 Az AI kinyeri:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>✅ Név, telefonszám, email</li>
                <li>✅ Lakcím</li>
                <li>✅ Pozíció/szakma</li>
                <li>✅ Tapasztalat (években)</li>
                <li>✅ Készségek</li>
                <li>✅ Nem (AI gender detection)</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCvImportDialog(false);
              setCvFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}>
              Mégse
            </Button>
            <Button 
              onClick={handleCvParse} 
              disabled={!cvFile || cvParsing}
              className="gap-2"
            >
              {cvParsing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  AI elemzés...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Elemzés AI-val
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Duplikáció kezelő Dialog */}
      <Dialog open={duplicateDialog} onOpenChange={setDuplicateDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-orange-500 flex items-center gap-2">
              ⚠️ Duplikáció találat!
            </DialogTitle>
            <DialogDescription>
              Már létezik hasonló dolgozó a rendszerben. Válaszd ki mit szeretnél tenni.
            </DialogDescription>
          </DialogHeader>
          
          {duplicateData && (
            <div className="grid grid-cols-2 gap-4">
              {/* Meglévő dolgozó */}
              <div className="border rounded-lg p-4 bg-red-50 dark:bg-red-950/20">
                <h4 className="font-semibold text-red-600 mb-3">🔴 Meglévő dolgozó</h4>
                <div className="space-y-2 text-sm">
                  <p><strong>Név:</strong> {duplicateData.existing_worker?.name}</p>
                  <p><strong>Telefon:</strong> {duplicateData.existing_worker?.phone || "-"}</p>
                  <p><strong>Email:</strong> {duplicateData.existing_worker?.email || "-"}</p>
                  <p><strong>Cím:</strong> {duplicateData.existing_worker?.address || "-"}</p>
                  <p><strong>Pozíció:</strong> {duplicateData.existing_worker?.position || "-"}</p>
                  <p><strong>Státusz:</strong> {duplicateData.existing_worker?.global_status || "-"}</p>
                  <p><strong>Megjegyzés:</strong> {duplicateData.existing_worker?.notes || "-"}</p>
                  <p className="text-muted-foreground text-xs mt-2">
                    Felvette: {duplicateData.existing_worker?.owner_name}
                  </p>
                </div>
              </div>
              
              {/* Új adat */}
              <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20">
                <h4 className="font-semibold text-green-600 mb-3">🟢 Új adat (amit most adtál meg)</h4>
                <div className="space-y-2 text-sm">
                  <p><strong>Név:</strong> {duplicateData.new_worker_data?.name}</p>
                  <p><strong>Telefon:</strong> {duplicateData.new_worker_data?.phone || "-"}</p>
                  <p><strong>Email:</strong> {duplicateData.new_worker_data?.email || "-"}</p>
                  <p><strong>Cím:</strong> {duplicateData.new_worker_data?.address || "-"}</p>
                  <p><strong>Pozíció:</strong> {duplicateData.new_worker_data?.position || "-"}</p>
                  <p><strong>Státusz:</strong> {duplicateData.new_worker_data?.global_status || "-"}</p>
                  <p><strong>Megjegyzés:</strong> {duplicateData.new_worker_data?.notes || "-"}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDuplicateDialog(false)}>
              Mégsem
            </Button>
            <Button 
              variant="default"
              onClick={handleUpdateExisting}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? "Frissítés..." : "🔄 Régi frissítése új adatokkal"}
            </Button>
            <Button 
              variant="default"
              onClick={handleForceCreate}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? "Létrehozás..." : "➕ Új létrehozása mindenképp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
